import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  Button, 
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  List as MUIList,
  ListItem as MUIListItem,
  ListItemText as MUIListItemText,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import { Delete, FilterList, Edit, NotificationsActive } from '@mui/icons-material';
import { api } from '../services/api';
import { useUser } from '../hooks/useUser';
import { getInvoiceAuditLogs, pingTodoSupplier } from '../services/api';

const TodoAdminComponent = () => {
  const { user } = useUser();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showFilters, setShowFilters] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentTodo, setCurrentTodo] = useState(null);
  const [amount, setAmount] = useState('');
  const [deduction, setDeduction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filters
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [completedFilter, setCompletedFilter] = useState('');
  const [domains, setDomains] = useState([]);
  const [users, setUsers] = useState([]);

  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedAuditTodo, setSelectedAuditTodo] = useState(null);

  const [pingDialogOpen, setPingDialogOpen] = useState(false);
  const [pingMessage, setPingMessage] = useState('');
  const [pingTodo, setPingTodo] = useState(null);

  useEffect(() => {
    if (user) {
      fetchTodos();
    }
  }, [user]);

  const fetchTodos = async (filters = {}) => {
    try {
      setLoading(true);
      // Apply any filters
      const params = {};
      if (userEmailFilter) params.user_email = userEmailFilter;
      if (domainFilter) params.domain = domainFilter;
      if (completedFilter !== '') params.completed = completedFilter === 'true';
      
      const response = await api.getAdminTodos(params);
      setTodos(response.data);
      
      // Extract unique domains and users for filter dropdowns
      const uniqueDomains = [...new Set(response.data.map(todo => todo.domain).filter(Boolean))];
      const uniqueUsers = [...new Set(response.data.map(todo => todo.user_email))];
      setDomains(uniqueDomains);
      setUsers(uniqueUsers);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching todos:', err);
      setError('Failed to fetch todos. Please try again later.');
      showSnackbar('Failed to fetch todos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) {
      return;
    }
    
    try {
      await api.deleteTodo(todoId);
      fetchTodos();
      showSnackbar('Todo deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting todo:', err);
      showSnackbar('Failed to delete todo', 'error');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const applyFilters = () => {
    fetchTodos({
      user_email: userEmailFilter,
      domain: domainFilter,
      completed: completedFilter !== '' ? completedFilter === 'true' : undefined
    });
  };

  const clearFilters = () => {
    setUserEmailFilter('');
    setDomainFilter('');
    setCompletedFilter('');
    fetchTodos();
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleEditTodo = (todo) => {
    setCurrentTodo(todo);
    setAmount(todo.amount || '');
    setDeduction(todo.deduction || '');
    setRejectionReason(todo.rejection_reason || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentTodo(null);
  };

  const handleUpdateTodo = async () => {
    try {
      const updates = {
        amount: amount === '' ? null : Number(amount),
        deduction: deduction === '' ? null : Number(deduction),
        rejection_reason: rejectionReason || null
      };
      
      await api.updateTodoAdmin(currentTodo.id, updates);
      showSnackbar('Todo updated successfully', 'success');
      fetchTodos();
      handleCloseEditDialog();
    } catch (err) {
      console.error('Error updating todo:', err);
      showSnackbar('Failed to update todo', 'error');
    }
  };

  const handleOpenAuditDrawer = async (todo) => {
    setSelectedAuditTodo(todo);
    setAuditDrawerOpen(true);
    setAuditLoading(true);
    try {
      const res = await getInvoiceAuditLogs(todo.supplier_name, todo.month_year);
      setAuditLogs(res.data);
    } catch (err) {
      setAuditLogs([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleCloseAuditDrawer = () => {
    setAuditDrawerOpen(false);
    setAuditLogs([]);
    setSelectedAuditTodo(null);
  };

  const handleOpenPingDialog = (todo) => {
    setPingTodo(todo);
    setPingMessage(`This is a reminder regarding your pending todo: ${todo.title}`);
    setPingDialogOpen(true);
  };

  const handleClosePingDialog = () => {
    setPingDialogOpen(false);
    setPingTodo(null);
    setPingMessage('');
  };

  const handleSendPing = async () => {
    try {
      await pingTodoSupplier(pingTodo.id, pingMessage);
      showSnackbar('Reminder email sent successfully', 'success');
      handleClosePingDialog();
    } catch (err) {
      showSnackbar('Failed to send reminder email', 'error');
    }
  };

  if (loading && todos.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <Typography variant="h6">Loading todos...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            All Todos (Admin View)
          </Typography>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {showFilters && (
          <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="user-select-label">User</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={userEmailFilter}
                    label="User"
                    onChange={(e) => setUserEmailFilter(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    {users.map(email => (
                      <MenuItem key={email} value={email}>{email}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="domain-select-label">Domain</InputLabel>
                  <Select
                    labelId="domain-select-label"
                    value={domainFilter}
                    label="Domain"
                    onChange={(e) => setDomainFilter(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    {domains.map(domain => (
                      <MenuItem key={domain} value={domain}>{domain}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="completed-select-label">Status</InputLabel>
                  <Select
                    labelId="completed-select-label"
                    value={completedFilter}
                    label="Status"
                    onChange={(e) => setCompletedFilter(e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="true">Completed</MenuItem>
                    <MenuItem value="false">Not Completed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="contained" onClick={applyFilters}>Apply</Button>
                  <Button variant="outlined" onClick={clearFilters}>Clear</Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {todos.length === 0 && !loading ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">
              No todos found matching the current filters.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={2}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Domain</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Deduction</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {todos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((todo) => (
                    <TableRow key={todo.id}>
                      <TableCell>{todo.title}</TableCell>
                      <TableCell>{todo.user_email}</TableCell>
                      <TableCell>{todo.domain || '--'}</TableCell>
                      <TableCell>
                        {todo.due_date ? new Date(todo.due_date).toLocaleDateString() : '--'}
                      </TableCell>
                      <TableCell>{todo.amount ? `$${todo.amount.toFixed(2)}` : '--'}</TableCell>
                      <TableCell>{todo.deduction ? `$${todo.deduction.toFixed(2)}` : '--'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={todo.completed ? 'Completed' : 'Open'} 
                          color={todo.completed ? 'success' : 'default'} 
                          size="small"
                        />
                        {todo.rejection_reason && (
                          <Chip
                            label="Rejected"
                            color="error"
                            size="small"
                            sx={{ ml: 1 }}
                            title={todo.rejection_reason}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditTodo(todo)}
                          title="Edit Todo"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(todo.id)}
                          title="Delete Todo"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenAuditDrawer(todo)}
                          sx={{ ml: 1 }}
                        >
                          Audit Log
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenPingDialog(todo)}
                          title="Send Reminder (Ping)"
                          sx={{ ml: 1 }}
                        >
                          <NotificationsActive fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={todos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Todo</DialogTitle>
          <DialogContent>
            {currentTodo && (
              <Box sx={{ pt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {currentTodo.title} - {currentTodo.user_email}
                </Typography>
                
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  margin="normal"
                  inputProps={{ step: "0.01" }}
                  helperText="Set the amount for this todo (optional)"
                />
                
                <TextField
                  fullWidth
                  label="Deduction"
                  type="number"
                  value={deduction}
                  onChange={(e) => setDeduction(e.target.value)}
                  margin="normal"
                  inputProps={{ step: "0.01" }}
                  helperText="Set the deduction amount (optional)"
                />
                
                <TextField
                  fullWidth
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  helperText="Provide a reason if rejecting this todo (optional)"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button onClick={handleUpdateTodo} variant="contained" color="primary">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Drawer anchor="right" open={auditDrawerOpen} onClose={handleCloseAuditDrawer}>
          <Box sx={{ width: 400, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Audit Log
            </Typography>
            {auditLoading ? (
              <CircularProgress />
            ) : auditLogs.length === 0 ? (
              <Typography>No audit logs found.</Typography>
            ) : (
              <Stepper orientation="vertical" nonLinear activeStep={-1}>
                {auditLogs.map((log, idx) => (
                  <Step key={log.id} completed>
                    <StepLabel>{`${log.action} by ${log.performed_by}`}</StepLabel>
                    <StepContent>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(log.performed_at).toLocaleString()}
                      </Typography>
                      {log.details && (
                        <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(log.details, null, 2)}</pre>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            )}
            <Button onClick={handleCloseAuditDrawer} sx={{ mt: 2 }} fullWidth variant="outlined">Close</Button>
          </Box>
        </Drawer>

        <Dialog open={pingDialogOpen} onClose={handleClosePingDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Send Reminder (Ping)</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Send a reminder email to <b>{pingTodo?.user_email}</b> for this todo.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Message"
              value={pingMessage}
              onChange={e => setPingMessage(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePingDialog}>Cancel</Button>
            <Button onClick={handleSendPing} variant="contained" color="primary">
              Send
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default TodoAdminComponent; 